import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import "./Profile.css";
import { getProfile } from "../../../api/auth";
import {
    ArrowLeft,
    BookOpen,
    Calendar,
    Fingerprint,
    Hash,
    Mail,
    PenSquare,
    Shield,
    UserRound,
    Zap,
} from "lucide-react";

export default function Profile() {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        let cancelled = false;

        const loadProfile = async () => {
            try {
                const data = await getProfile();
                if (cancelled) return;
                setUser(data);
            } catch (err) {
                if (cancelled) return;
                const statusCode = err?.response?.status;
                if (statusCode === 401 || statusCode === 403) {
                    setError("Your session expired. Please log in again.");
                } else {
                    setError(err?.response?.data?.message || err?.message || "Failed to load profile.");
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        loadProfile();

        return () => {
            cancelled = true;
        };
    }, []);

    const initials = useMemo(() => {
        const source = user?.name || user?.email || "User";
        const parts = String(source).trim().split(/\s+/).filter(Boolean);
        if (parts.length === 0) return "U";
        if (parts.length === 1) return parts[0].slice(0, 1).toUpperCase();
        return `${parts[0].slice(0, 1)}${parts[1].slice(0, 1)}`.toUpperCase();
    }, [user?.name, user?.email]);

    const createdDate = useMemo(() => {
        if (!user?.createdAt) return "—";
        try {
            return new Date(user.createdAt).toLocaleDateString(undefined, {
                year: "numeric",
                month: "long",
                day: "numeric",
            });
        } catch {
            return "—";
        }
    }, [user?.createdAt]);

    const createdDateTime = useMemo(() => {
        if (!user?.createdAt) return "—";
        try {
            return new Date(user.createdAt).toLocaleString();
        } catch {
            return "—";
        }
    }, [user?.createdAt]);

    const roleLabel = (user?.role || "User").toString();

    const shortId = useMemo(() => {
        const id = user?._id;
        if (!id || typeof id !== "string") return "—";
        if (id.length <= 14) return id;
        return `${id.slice(0, 8)}…${id.slice(-4)}`;
    }, [user?._id]);

    if (loading) {
        return (
            <section className="pf" aria-label="Profile loading">
                <div className="pf-skel pf-skel-topbar" />
                <div className="pf-skel pf-skel-hero" />
                <div className="pf-grid pf-grid--loading">
                    <div className="pf-skel pf-skel-card-lg" />
                    <div className="pf-skel pf-skel-card-md" />
                </div>
            </section>
        );
    }

    if (error) {
        return (
            <section className="pf" aria-label="Profile error">
                <div className="pf-empty pf-empty--error" role="alert">
                    <div className="pf-empty-icon" aria-hidden>
                        <Shield size={28} strokeWidth={2} />
                    </div>
                    <h1 className="pf-empty-title">Profile unavailable</h1>
                    <p className="pf-empty-text">{error}</p>
                    <Link className="pf-btn pf-btn--primary" to="/dashboard">
                        Back to Dashboard
                    </Link>
                </div>
            </section>
        );
    }

    if (!user) {
        return (
            <section className="pf" aria-label="Profile empty">
                <div className="pf-empty">
                    <h1 className="pf-empty-title">No profile found</h1>
                    <p className="pf-empty-text">We could not find a user profile for this session.</p>
                    <Link className="pf-btn pf-btn--primary" to="/dashboard">
                        Back to Dashboard
                    </Link>
                </div>
            </section>
        );
    }

    return (
        <section className="pf" aria-label="User profile">
            <nav className="pf-topbar" aria-label="Breadcrumb">
                <Link to="/dashboard" className="pf-back">
                    <ArrowLeft size={18} strokeWidth={2} aria-hidden />
                    Dashboard
                </Link>
            </nav>

            <section className="pf-hero" aria-label="Profile summary">
                <div className="pf-hero-glow" aria-hidden />
                <div className="pf-hero-inner">
                    <div className="pf-avatar" aria-hidden>
                        {initials}
                    </div>
                    <div className="pf-hero-copy">
                        <span className="pf-role-pill">{roleLabel}</span>
                        <h2 className="pf-display-name">{user.name || "Account"}</h2>
                        <p className="pf-email">
                            <Mail size={16} strokeWidth={2} aria-hidden />
                            <span>{user.email || "—"}</span>
                        </p>
                    </div>
                </div>
            </section>

            <div className="pf-grid">
                <article className="pf-card pf-card--main" aria-labelledby="pf-account-heading">
                    <header className="pf-card-head pf-card-head--fancy">
                        <div className="pf-card-head-icon" aria-hidden>
                            <Fingerprint size={20} strokeWidth={2} />
                        </div>
                        <div className="pf-card-head-copy">
                            <p className="pf-card-eyebrow">Identity</p>
                            <h2 id="pf-account-heading" className="pf-card-title">
                                Account details
                            </h2>
                        </div>
                    </header>

                    <div className="pf-card-body">
                    <ul className="pf-fields">
                        <li className="pf-field">
                            <div className="pf-field-label">
                                <Hash size={16} strokeWidth={2} aria-hidden />
                                User ID
                            </div>
                            <div className="pf-field-value">
                                <code className="pf-code" title={user._id || ""}>
                                    {shortId}
                                </code>
                            </div>
                        </li>
                        <li className="pf-field">
                            <div className="pf-field-label">
                                <UserRound size={16} strokeWidth={2} aria-hidden />
                                Name
                            </div>
                            <div className="pf-field-value">{user.name || "—"}</div>
                        </li>
                        <li className="pf-field">
                            <div className="pf-field-label">
                                <Mail size={16} strokeWidth={2} aria-hidden />
                                Email
                            </div>
                            <div className="pf-field-value">{user.email || "—"}</div>
                        </li>
                        <li className="pf-field">
                            <div className="pf-field-label">
                                <Shield size={16} strokeWidth={2} aria-hidden />
                                Role
                            </div>
                            <div className="pf-field-value">{roleLabel}</div>
                        </li>
                        <li className="pf-field pf-field--stack">
                            <div className="pf-field-label">
                                <Calendar size={16} strokeWidth={2} aria-hidden />
                                Created
                            </div>
                            <div className="pf-field-value">
                                <span className="pf-date-primary">{createdDate}</span>
                                <span className="pf-date-sub">{createdDateTime}</span>
                            </div>
                        </li>
                    </ul>
                    </div>
                </article>

                <aside className="pf-card pf-card--aside" aria-labelledby="pf-aside-heading">
                    <header className="pf-card-head pf-card-head--fancy">
                        <div className="pf-card-head-icon pf-card-head-icon--aside" aria-hidden>
                            <Zap size={20} strokeWidth={2} />
                        </div>
                        <div className="pf-card-head-copy">
                            <p className="pf-card-eyebrow">Shortcuts</p>
                            <h2 id="pf-aside-heading" className="pf-card-title">
                                Quick actions
                            </h2>
                        </div>
                    </header>

                    <div className="pf-card-body pf-card-body--aside">
                    <div className="pf-pills">
                        <div className="pf-pill pf-pill--success">
                            <span className="pf-pill-dot" aria-hidden />
                            <span className="pf-pill-text">Session active</span>
                        </div>
                        <div className="pf-pill">
                            <Shield size={14} strokeWidth={2} aria-hidden />
                            <span className="pf-pill-text">{roleLabel}</span>
                        </div>
                    </div>

                    <div className="pf-actions">
                        <Link to="/dashboard/add-blog" className="pf-action pf-action--primary">
                            <PenSquare size={18} strokeWidth={2} aria-hidden />
                            Create blog
                        </Link>
                        <Link to="/dashboard/blog-list" className="pf-action pf-action--ghost">
                            <BookOpen size={18} strokeWidth={2} aria-hidden />
                            All posts
                        </Link>
                    </div>

                    {/*<p className="pf-note">*/}
                    {/*    */}
                    {/*</p>*/}
                    </div>
                </aside>
            </div>
        </section>
    );
}
